/**
 * App-wide account chip — guest silhouette when signed out, initials when signed in.
 * Shown on every route via GlobalTopBarChrome.
 */
import React, { useMemo, useState } from 'react';
import {
  Avatar,
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import AccountCircleRounded from '@mui/icons-material/AccountCircleRounded';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { useAuth } from 'hooks/useAuth';
import {
  authPath,
  isFusionMsiBrandedPath,
} from 'features/decisionPoint/marketing/fusionMsiMarketingRouting';
import { PRODUCT_NAME } from 'features/decisionPoint/data/fusionMsiBranding';
import {
  CARESTART_SPLASH_WORDMARK,
  isCareStartSplashPath,
} from 'features/decisionPoint/marketing/careStartSplashCopy';
import { dpUserInitials } from 'features/decisionPoint/utils/dpUserInitials';
import { isAsiMarketingHost } from 'features/asi/marketing/asiMarketingRouting';
import { getPlatformBranding } from 'config/platformBranding';
import { shouldApplyVoxPontBranding } from 'pages/voxpontmarketing/voxpontMarketingRouting';
import { isUnifiedProductChromeEnabled } from 'components/layout/unifiedProductChrome';

export const AUTH_CHIP_SIZE = 36;

/**
 * Build the sign-in href for the current surface.
 * When unified chrome is on and the path/host is VoxPont-branded, use /voxpont-auth
 * (apex voxpont.com is not an app subdomain, so /auth there is still HealthDesk).
 *
 * @param {string} pathname
 * @param {string} [search]
 * @returns {string}
 */
export function resolveSignInHref(pathname, search = '') {
  const next = `${pathname}${search || ''}`;
  if (isUnifiedProductChromeEnabled() && shouldApplyVoxPontBranding(pathname)) {
    const params = new URLSearchParams();
    params.set('next', next);
    try {
      const current = new URLSearchParams(
        (search || '').startsWith('?') ? search.slice(1) : search || ''
      );
      const origin = current.get('origin');
      if (origin === 'byoi' || origin === 'voxpont_byoi') {
        params.set('origin', 'byoi');
      }
    } catch {
      // ignore malformed search
    }
    return `/voxpont-auth?${params.toString()}`;
  }
  if (isFusionMsiBrandedPath(pathname)) {
    return authPath({ next });
  }
  return `/auth?next=${encodeURIComponent(next)}`;
}

function AppAuthIndicator() {
  const { user, userLoading, logout } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [signingOut, setSigningOut] = useState(false);

  const isFusionSurface = isFusionMsiBrandedPath(location.pathname);
  const fusionBrandName = isCareStartSplashPath(location.pathname)
    ? CARESTART_SPLASH_WORDMARK
    : PRODUCT_NAME;
  const isAsiHost = isAsiMarketingHost();
  const isVoxPontSurface =
    isUnifiedProductChromeEnabled() && shouldApplyVoxPontBranding(location.pathname);
  const branding = getPlatformBranding();
  const signInHref = useMemo(
    () => resolveSignInHref(location.pathname, location.search),
    [location.pathname, location.search]
  );

  const initials = dpUserInitials(user);
  const isSignedIn = Boolean(user);

  const handleSignOut = async () => {
    setSigningOut(true);
    try {
      await logout();
      setOpen(false);
    } finally {
      setSigningOut(false);
    }
  };

  const guestAriaLabel = isVoxPontSurface
    ? 'VoxPont sign in'
    : isAsiHost
      ? `${branding.name} sign in`
      : isFusionSurface
        ? `${fusionBrandName} sign in`
        : 'Sign in';
  const guestDialogTitle = isVoxPontSurface
    ? 'VoxPont'
    : isAsiHost
      ? branding.name
      : isFusionSurface
        ? fusionBrandName
        : 'Sign in';
  const guestDialogBody = isVoxPontSurface
    ? 'Sign in to access your VoxPont account and live translation sessions.'
    : isAsiHost
      ? 'Sign in to access your Applied SuperIntelligence account.'
      : isFusionSurface
        ? 'Sign in to access DecisionPoint, admin tools, and reviewer workflows.'
        : 'Sign in to access your FusionMSI account and apps.';

  return (
    <>
      <IconButton
        onClick={() => setOpen(true)}
        aria-label={isSignedIn ? 'Account menu' : guestAriaLabel}
        sx={{
          p: 0.25,
          borderRadius: '50%',
          border: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
          '&:hover': { bgcolor: 'action.hover' },
        }}
      >
        {userLoading ? (
          <Box
            sx={{
              width: AUTH_CHIP_SIZE,
              height: AUTH_CHIP_SIZE,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CircularProgress size={20} />
          </Box>
        ) : isSignedIn ? (
          <Avatar
            sx={{
              width: AUTH_CHIP_SIZE,
              height: AUTH_CHIP_SIZE,
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              typography: 'body2',
              fontWeight: 600,
            }}
          >
            {initials}
          </Avatar>
        ) : (
          <AccountCircleRounded
            sx={{ width: AUTH_CHIP_SIZE, height: AUTH_CHIP_SIZE, color: 'action.active' }}
          />
        )}
      </IconButton>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{isSignedIn ? 'Account' : guestDialogTitle}</DialogTitle>
        <DialogContent>
          {isSignedIn ? (
            <Stack spacing={2}>
              <Box>
                {user.displayName && (
                  <Typography variant="subtitle1">{user.displayName}</Typography>
                )}
                <Typography variant="body2" color="text.secondary">
                  {user.email}
                </Typography>
              </Box>
              <LoadingButton
                variant="outlined"
                color="inherit"
                loading={signingOut}
                onClick={handleSignOut}
              >
                Sign out
              </LoadingButton>
            </Stack>
          ) : (
            <Stack spacing={2}>
              <Typography variant="body2" color="text.secondary">
                {guestDialogBody}
              </Typography>
              <LoadingButton
                component={RouterLink}
                to={signInHref}
                variant="contained"
                onClick={() => setOpen(false)}
              >
                Sign in
              </LoadingButton>
            </Stack>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

export default AppAuthIndicator;
